/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Badge, Box, ClickAwayListener, Grow, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Paper } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import CommandBar from "../../../../Basis/Components/CommandBar";
import CommandBarButton from "../../../../Basis/Components/CommandBarButton";
import CommandBarMenu from "../../../../Basis/Components/CommandBarMenu";
import ViewHeader from "../../../../Basis/Components/ViewHeader";
import YesNoDialog from "../../../../Basis/Components/YesNoDialog";
import { BellIcon, CheckIcon, EllipsisHIcon, ExclamationIcon, XIcon } from "../../../../Basis/FontAwesome";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBenachrichtigungen";
import AppNotificationIcon from "./AppNotificationIcon";
import AppNotificationLink from "./AppNotificationLink";
import AppNotificationsModel from "./AppNotificationsModel";
import "./AppNotifications.scss";

export default function AppNotifications() {
    const vm = new AppNotificationsModel();
    const { t } = useTranslation();

    const commandBarMenuItems = [
        <CommandBarButton
            key={1}
            caption={t(translationKeys.appNotifications.markAllRead)}
            icon={<CheckIcon />}
            onClick={vm.markiereAlleItemsAlsGelesen}
            disabled={!vm.kannAlleMarkierenAlsGelesen}
        />
    ];

    return <>
        <div className="lan-appnotifications" onClick={vm.oeffneDrawer}>
            <Badge
                badgeContent={vm.anzahlUngesehenerItems}
                max={99}
                overlap="rectangular"
                color="secondary"
                className="lan-appnotifications__badge"
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <BellIcon className={`lan-appnotifications__bell${vm.klingeln ? " fa-ring" : ""}`} />
            </Badge>
            <EllipsisHIcon className={`lan-appnotifications__running${vm.hatLaufendeItems ? " fa-pulse2" : " lan-appnotifications__invisible"}`} />
        </div>

        <Grow in={vm.hatUngetoasteteItems} timeout={300} >
            <div className="lan-appnotifications__toast">
                <Paper className="lan-appnotifications__toastpaper">
                    <List dense={true} className="lan-appnotifications__list">
                        {vm.ungetoasteteItems.map((item, index) => (
                            <ListItem key={item.benachrichtigung.benachrichtigungId} className="lan-appnotifications__listItem">
                                <Box sx={{ display: "flex", flexWrap: "wrap", margin: ".5rem .5rem" }}>
                                    <ListItemIcon>
                                        <AppNotificationIcon symbol={item.benachrichtigung.symbol} />
                                    </ListItemIcon>
                                </Box>
                                <ListItemText
                                    primary={<AppNotificationLink item={item} onClick={m => vm.navigiereZuReferenzFuerToast(item)} />}
                                    secondary={
                                        <div className="lan-appnotifications__listitemunterschrift-clarity">
                                            {item.benachrichtigung.text}
                                            {(item.benachrichtigung.details.length > 0) &&
                                                <ul className="lan-appnotifications__listitemdetails">
                                                    {item.benachrichtigung.details.map((detail) => (
                                                        <li key={detail.detailId}>{detail.text}</li>
                                                    ))}
                                                </ul>
                                            }
                                        </div>
                                    }
                                    secondaryTypographyProps={{ component: "div" }}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        aria-label="gelesen"
                                        onClick={() => vm.markiereItemAlsGetoasted(item)}
                                        size="large">
                                        <XIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </div>
        </Grow>

        <Drawer
            className="lan-appnotifications__drawer"
            anchor="right"
            classes={{ paper: "lan-appnotifications__drawer__paper" }}
            open={vm.state.istDrawerOffen}
        >
            <ClickAwayListener onClickAway={vm.schliesseDrawer}>
                <div className="lan-appnotifications__background">
                    <ViewHeader title={t(translationKeys.appNotifications.notifications)} onClose={vm.schliesseDrawer} />
                    <CommandBar>
                        <CommandBarMenu items={commandBarMenuItems} />
                        {commandBarMenuItems}
                    </CommandBar>
                    <Paper className="lan-appnotifications__drawerpaper">
                        <List dense={true} className="lan-appnotifications__list">
                            {vm.ungeleseneItems.map((item, index) => (
                                <ListItem key={item.benachrichtigung.benachrichtigungId} className="lan-appnotifications__listitem">
                                    <Box sx={{ display: "flex", flexWrap: "wrap", margin: ".5rem .5rem" }}>
                                        <ListItemIcon>
                                            <AppNotificationIcon symbol={item.benachrichtigung.symbol} />
                                        </ListItemIcon>
                                        {!item.benachrichtigung.istAlleLesbar && <ListItemIcon>
                                            <ExclamationIcon className="lan-appnotifications__icon lan-appnotifications__icon--default" />
                                        </ListItemIcon>}
                                    </Box>
                                    <ListItemText
                                        primary={<AppNotificationLink item={item} onClick={m => vm.navigiereZuReferenzFuerGelesen(item)} />}
                                        secondary={
                                            <div className="lan-appnotifications__listitemunterschrift-clarity">
                                                {item.benachrichtigung.text}
                                                {(item.benachrichtigung.details.length > 0) &&
                                                    <ul className="lan-appnotifications__listitemdetails">
                                                        {item.benachrichtigung.details.map((detail) => (
                                                            <li key={detail.detailId}>{detail.text}</li>
                                                        ))}
                                                    </ul>
                                                }
                                            </div>
                                        }
                                        secondaryTypographyProps={{ component: "div" }}
                                    />
                                    <ListItemSecondaryAction>
                                        {item.benachrichtigung.kannMarkierenAlsGelesen &&
                                        <IconButton
                                            edge="end"
                                            aria-label="gelesen"
                                            onClick={() => vm.oeffneDialogOderMarkiereItemAlsGelesen(item)}
                                            size="large">
                                            <XIcon />
                                        </IconButton>
                                        }
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </div>
            </ClickAwayListener>
            <YesNoDialog
                isOpen={!!vm.state.itemImDialog}
                dialogCaption={t(translationKeys.appNotifications.benachrichtigungErledigt)}
                dialogText={t(translationKeys.appNotifications.benachrichtigungErledigtText)}
                onYes={() => {
                    if (vm.state.itemImDialog != null) {
                        vm.markiereItemAlsGelesen(vm.state.itemImDialog);
                        vm.schliesseDialog();
                    }
                }}
                onNo={vm.schliesseDialog}
                variant="warning"
            />
        </Drawer>
    </>;
}